"use client";

import {
    HyperElements,
    UnifiedCheckout,
    useHyper,
    useWidgets,
} from "@juspay-tech/react-hyper-js";
import { loadHyper } from "@juspay-tech/hyper-js";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { toast } from "react-toastify";
import { api } from "~/utils/api";
import { googleAnalyticsEvent } from "~/utils/googleAnalyticsUtils";
import { useMe } from "~/hooks/useMe";
import { FilledButton } from "~/components/buttons/filled-button";
import { OutlineButton } from "~/components/buttons/outline-button";
import { formatMoney } from "~/utils/formatters";
import type { NypOfferDetails } from "./name-your-price-form";
import { Tooltip } from "~/components/tooltip";
import { Info } from "~/components/icons/info";
import dayjs from "dayjs";

let hyperPromise: Promise<any> | undefined;
if (typeof window !== "undefined") {
    hyperPromise = loadHyper(
        process.env.NEXT_PUBLIC_HYPERSWITCH_PUBLISHABLE_KEY || ""
    );
}

// ── Inner form: rendered inside HyperElements context ──────────────────────
function PaymentForm({
    offerDetails,
    holdMultiplier,
    courseName,
    paymentId,
    updateOfferSession,
    onBack,
    onOfferSent,
}: {
    offerDetails: NypOfferDetails;
    holdMultiplier: number;
    courseName: string;
    paymentId: string;
    updateOfferSession: (amountInCents: number) => Promise<string>;
    onBack: () => void;
    onOfferSent: () => void;
}) {
    const [paymentError, setPaymentError] = useState<string | null>(null);
    const hyper = useHyper();
    const widgets = useWidgets();
    const { mutateAsync: createOffer, isLoading: isCreating } = api.nameYourPriceOffer.createOffer.useMutation();
    const { refetch: refetchOffers } = api.nameYourPriceOffer.getMyOffers.useQuery({}, { enabled: false });

    const [isUpdating, setIsUpdating] = useState(false);
    const [isPaying, setIsPaying] = useState(false);
    const [userConsent, setUserConsent] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [errors, setErrors] = useState({ userConsent: "", termsOfService: "" });
    const lastSyncedRef = useRef<number>(1);
    const userConsentRef = useRef(false);
    const termsRef = useRef(false);

    useEffect(() => { userConsentRef.current = userConsent; }, [userConsent]);
    useEffect(() => { termsRef.current = termsAccepted; }, [termsAccepted]);

    const totalPrice = (offerDetails.pricePerPlayer * offerDetails.players) / 100;
    const holdAmount = Math.round(totalPrice * holdMultiplier * 100);

    // Sync payment intent amount once on mount
    useEffect(() => {
        if (!(hyper && widgets)) return;
        if (holdAmount <= 0 || holdAmount === lastSyncedRef.current) return;
        const sync = async () => {
            setIsUpdating(true);
            let secret = "";
            try {
                await hyper.initiateUpdateIntent();
                secret = await updateOfferSession(holdAmount);
                lastSyncedRef.current = holdAmount;
            } catch (e) {
                console.error("[NypPaymentForm] updateIntent error:", e);
            } finally {
                try { await hyper.completeUpdateIntent(secret); } catch { /* non-blocking — sync retries on next amount change */ }
                setIsUpdating(false);
            }
        };
        void sync();
    }, [hyper, widgets]);

    const isFormValid = userConsent && termsAccepted;

    // Build wallet return URL — strip existing nyp_ params, then add nyp_return=1
    const walletReturnUrl = typeof window !== "undefined"
        ? (() => {
            const url = new URL(window.location.href);
            url.searchParams.set("nyp_return", "1");
            return url.toString();
        })()
        : "";

    const unifiedCheckoutOptions = {
        wallets: {
            walletReturnUrl,
            applePay: "auto",
            googlePay: "auto",
            style: { theme: "dark", buttonRadius: 70 },
        },
        paymentMethodOrder: ["card", "google_pay", "apple_pay"],
        billingAddress: { isUseBillingAddress: true, usePrefilledValues: "never" },
        branding: "never",
        hideExpiredPaymentMethods: true,
        displaySavedPaymentMethodsCheckbox: false,
        layout: { savedMethodCustomization: { groupingBehavior: "groupByPaymentMethods" } },
        disabled: isUpdating || isPaying || !isFormValid,
    };

    // eslint-disable-next-line @typescript-eslint/require-await -- must return a Promise for the wallet button's onPaymentButtonClick contract
    const handleWalletClick = async (walletEvent?: unknown) => {
        if (!userConsentRef.current) {
            setErrors(e => ({ ...e, userConsent: "Please accept the user consent." }));
            document.getElementById("nyp-user-consent-checkbox")?.scrollIntoView({ behavior: "smooth", block: "center" });
            throw new Error("User consent not accepted");
        }
        if (!termsRef.current) {
            setErrors(e => ({ ...e, termsOfService: "Please accept the Terms of Service." }));
            document.getElementById("nyp-terms-of-service-checkbox")?.scrollIntoView({ behavior: "smooth", block: "center" });
            throw new Error("Terms not accepted");
        }
        setErrors({ userConsent: "", termsOfService: "" });
        // Distinguish wallet type (Google/Apple Pay) instead of the generic place-offer
        // event — mirrors the offer checkout so a GPay NyP offer logs as btn_nyp_google_pay.
        const w = (walletEvent ?? {}) as Record<string, unknown>;
        const walletType = String(
            w.paymentMethodType ?? w.paymentType ?? w.walletName ?? w.paymentMethod ?? w.type ?? ""
        ).toLowerCase();
        googleAnalyticsEvent({
            action: walletType.includes("google")
                ? "btn_nyp_google_pay"
                : walletType.includes("apple")
                    ? "btn_nyp_apple_pay"
                    : "btn_nyp_wallet_pay",
            params: {
                payment_method: walletType || "wallet",
                flow: "nyp",
                selected_value: offerDetails.pricePerPlayer / 100,
            },
        });
        // Persist offer details before wallet redirect so we can finalise on return
        localStorage.setItem("nyp_pending_offer", JSON.stringify({
            offerDetails,
            paymentId,
            holdAmount,
        }));
    };

    const handleSubmit = async () => {
        if (!userConsent) {
            setErrors(e => ({ ...e, userConsent: "Please accept the user consent." }));
            document.getElementById("nyp-user-consent-checkbox")?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }
        if (!termsAccepted) {
            setErrors(e => ({ ...e, termsOfService: "Please accept the Terms of Service." }));
            document.getElementById("nyp-terms-of-service-checkbox")?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }
        setErrors({ userConsent: "", termsOfService: "" });
        setPaymentError(null);
        googleAnalyticsEvent({
            action: "btn_nyp_place_offer",
            params: { selected_value: offerDetails.pricePerPlayer / 100 },
        });
        setIsPaying(true);
        try {
            const result = await hyper.confirmPayment({
                confirmParams: { return_url: window.location.href },
                redirect: "if_required",
            }) as {
                error?: { message?: string };
                status?: string;
                payment_id?: string;
                payment_method_id?: string;
            } | undefined;
            if (result?.error) {
                setPaymentError(result.error.message ?? "Payment failed. Please try again.");
                return;
            }
            // Handle all payment statuses that indicate an authorized/captured hold
            const successStatuses = ["succeeded", "requires_capture", "authorized", "charged", "processing"];
            if (result?.status && successStatuses.includes(result.status)) {
                await finalizeOffer(
                    result.payment_id ?? paymentId,
                    result.payment_method_id
                );
            } else if (result?.status) {
                // Unknown status — log and attempt to finalize anyway using known paymentId
                console.warn("[NyP] Unexpected payment status:", result.status, "— attempting finalizeOffer with known paymentId");
                await finalizeOffer(paymentId, undefined);
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : "Payment failed. Please try again.";
            setPaymentError(message);
        } finally {
            setIsPaying(false);
        }
    };

    const finalizeOffer = async (finalPaymentId: string, paymentMethodId?: string) => {
        await createOffer(
            {
                courseId: offerDetails.courseId,
                date: offerDetails.date,
                startTime: offerDetails.startTime,
                endTime: offerDetails.endTime,
                players: offerDetails.players,
                pricePerPlayer: offerDetails.pricePerPlayer,
                preAuthId: finalPaymentId,
                holdAmount,
                paymentMethodId: paymentMethodId ?? undefined,
                redirectHref: window.location.origin,
            },
            {
                onSuccess: () => {
                    toast.success("Your offer has been sent!", {
                        progressStyle: { background: "var(--primary-color)" },
                    });
                    void refetchOffers();
                    onOfferSent();
                },
                onError: () => {
                    setPaymentError("Offer submission failed. Please contact support.");
                },
            }
        );
    };

    const formatTime = (value: number) => {
        const hour = Math.floor(value / 100);
        const minute = value % 100;
        const h = hour % 12 === 0 ? 12 : hour % 12;
        const period = hour < 12 ? "AM" : "PM";
        return `${h}:${minute === 0 ? "00" : minute} ${period}`;
    };

    return (
        <div className="flex flex-col">
            {/* Title — same typography as the NameYourPriceForm header this card
                replaces, so stepping into payment doesn't change the page's type scale */}
            <div className="px-4 md:px-8 mb-2 mt-2 md:mt-5">
                <h1 className="md:text-center text-[1.25rem] capitalize text-secondary-black md:text-[2rem]">
                    Confirm Your Offer
                </h1>
                <h2 className="md:text-center text-[0.875rem] text-primary-gray md:text-[1rem] mt-1">
                    Review your offer and authorize the hold — you&apos;re only charged if it&apos;s accepted
                </h2>
            </div>

            <hr />

            <div className="flex flex-col gap-4 px-4 py-4 md:px-8 md:py-6">
                {/* Offer Summary — same plain (unsized) text as the normal checkout's
                price summary (offer-checkout-form.tsx), not a smaller pill-card scale */}
                <div className="rounded-xl bg-secondary-white p-4 space-y-2">
                    <h3 className="font-semibold text-secondary-black">Your Offer</h3>
                    <div className="flex justify-between">
                        <span>Date</span>
                        <span>{dayjs(offerDetails.date).format("ddd, MMM D, YYYY")}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Time window</span>
                        <span>{formatTime(offerDetails.startTime)} – {formatTime(offerDetails.endTime)}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Players</span>
                        <span>{offerDetails.players}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Offer price</span>
                        <span>{formatMoney(offerDetails.pricePerPlayer / 100)} / player</span>
                    </div>
                    <hr className="border-gray-200" />
                    <div className="flex justify-between text-blue-600">
                        <span className="flex items-start">
                            Pre-authorized hold
                            <Tooltip
                                trigger={<Info className="ml-2 h-4 w-4 mt-0.5" />}
                                content="Released if not accepted"
                            />
                        </span>
                        <span className="font-semibold">{formatMoney(holdAmount / 100)}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Offer amount</span>
                        <span>{formatMoney(totalPrice)}</span>
                    </div>
                    <p className="text-[0.75rem] text-primary-gray italic mt-1">
                        Note: This amount does not include taxes.
                    </p>
                </div>

                {/* Payment widget */}
                <div className={`transition-opacity duration-200 ${isUpdating ? "opacity-50 pointer-events-none" : ""}`}>
                    <UnifiedCheckout
                        id="nyp-unified-checkout"
                        options={unifiedCheckoutOptions}
                        onPaymentButtonClick={handleWalletClick}
                        // eslint-disable-next-line @typescript-eslint/no-empty-function -- required prop, no-op is intentional
                        onChange={() => { }}
                    />
                </div>

                {/* User consent — matches offer-checkout-form exactly */}
                <div className={`flex flex-col transition-opacity duration-200 ${isUpdating || isPaying ? "opacity-50 pointer-events-none" : ""}`}>
                    <label
                        htmlFor="nyp-user-consent-checkbox"
                        className="flex gap-3 rounded-xl border p-3 text-sm"
                        style={{ borderColor: userConsent ? "" : "red" }}
                    >
                        <input
                            id="nyp-user-consent-checkbox"
                            name="nyp-user-consent-checkbox"
                            type="checkbox"
                            checked={userConsent}
                            disabled={isUpdating || isPaying}
                            onChange={() => {
                                setUserConsent(v => !v);
                                setErrors(e => ({ ...e, userConsent: "" }));
                            }}
                        />
                        <span className="cursor-pointer text-[0.875rem] font-bold text-justify">
                            I authorize automatic payment if my offer is accepted.
                        </span>
                    </label>
                    {errors.userConsent && (
                        <p className="text-red text-sm mt-1 ml-1">{errors.userConsent}</p>
                    )}
                </div>

                {/* Terms of Service — matches offer-checkout-form exactly */}
                <div className={`flex flex-col transition-opacity duration-200 ${isUpdating || isPaying ? "opacity-50 pointer-events-none" : ""}`}>
                    <label
                        htmlFor="nyp-terms-of-service-checkbox"
                        className="flex items-start gap-3 rounded-xl border p-3 text-sm"
                        style={{ borderColor: termsAccepted ? "" : "red" }}
                    >
                        <input
                            id="nyp-terms-of-service-checkbox"
                            name="nyp-terms-of-service-checkbox"
                            type="checkbox"
                            className="mt-1"
                            checked={termsAccepted}
                            disabled={isUpdating || isPaying}
                            onChange={() => {
                                setTermsAccepted(v => !v);
                                setErrors(e => ({ ...e, termsOfService: "" }));
                            }}
                        />
                        <span className="cursor-pointer text-[0.875rem] font-bold text-justify">
                            I understand and agree that if my offer is accepted then I am purchasing a non-refundable, non-cancellable and non-changeable tee time.
                            By checking the box and completing this reservation, I agree to the Terms of Service.{" "}
                            <Link href="/terms-of-service" className="text-blue-600 underline" target="_blank">
                                Terms of Service
                            </Link>.
                        </span>
                    </label>
                    {errors.termsOfService && (
                        <p className="text-red text-sm mt-1 ml-1">{errors.termsOfService}</p>
                    )}
                </div>

                {paymentError && (
                    <p className="text-red text-sm text-center">{paymentError}</p>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                    <FilledButton
                        onClick={handleSubmit}
                        disabled={isCreating || isPaying || isUpdating}
                        className="flex-1 disabled:opacity-50"
                    >
                        {isPaying || isCreating ? "Processing..." : "Place Offer"}
                    </FilledButton>
                    <OutlineButton
                        onClick={onBack}
                        disabled={isPaying || isCreating}
                        className="flex-1 border border-gray-300 disabled:opacity-50"
                    >
                        Back
                    </OutlineButton>
                </div>

                <p className="text-center italic text-primary-gray text-[0.75rem] px-2">
                    Your card is charged only upon acceptance. The hold is released automatically if no one accepts.
                </p>
            </div>
        </div>
    );
}

// ── Outer wrapper: initializes Hyperswitch session ──────────────────────────
export function NameYourPricePaymentStep({
    offerDetails,
    courseName,
    onBack,
    onOfferSent,
    onInitError,
}: {
    offerDetails: NypOfferDetails;
    courseName: string;
    onBack: () => void;
    onOfferSent: () => void;
    onInitError: (message: string) => void;
}) {
    const { user } = useMe();
    const { mutateAsync: buildSession } = api.checkout.buildOfferCheckoutSession.useMutation();
    const { mutateAsync: updatePaymentAmount } = api.checkout.updateOfferPaymentAmount.useMutation();
    const { data: offerHoldExtraPercent } = api.bookingOffers.getOfferHoldPercentage.useQuery();
    const holdMultiplier = 1 + (offerHoldExtraPercent ?? 50) / 100;

    const [options, setOptions] = useState<{ clientSecret: string; paymentId: string; cartId: string } | null>(null);
    const hasInitRef = useRef(false);

    useEffect(() => {
        if (!user?.id || hasInitRef.current) return;
        hasInitRef.current = true;
        buildSession({ ownerId: user.id, offeredAmount: 1, courseName })
            .then(res => {
                setOptions({
                    clientSecret: res.clientSecret ?? "",
                    paymentId: res.paymentId ?? "",
                    cartId: res.cartId ?? "",
                });
            })
            .catch(err => {
                onInitError("Failed to initialize payment. Please try again.");
                console.error("[NypPaymentStep] buildSession error:", err);
                onBack();
            });
    }, [user?.id]);

    const updateOfferSession = useCallback(
        async (amountInCents: number): Promise<string> => {
            const result = await updatePaymentAmount({
                paymentId: options?.paymentId ?? "",
                amountInCents,
                courseName,
            });
            return result.clientSecret ?? "";
        },
        [updatePaymentAmount, options?.paymentId, courseName]
    );

    if (!options || !hyperPromise) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="animate-pulse text-primary-gray text-[0.875rem]">
                    Preparing payment...
                </div>
            </div>
        );
    }

    return (
        <HyperElements options={{ clientSecret: options.clientSecret, paymentId: options.paymentId, appearance: { theme: "default" } }} hyper={hyperPromise}>
            <PaymentForm
                offerDetails={offerDetails}
                holdMultiplier={holdMultiplier}
                courseName={courseName}
                paymentId={options.paymentId}
                updateOfferSession={updateOfferSession}
                onBack={onBack}
                onOfferSent={onOfferSent}
            />
        </HyperElements>
    );
}
